import type { ContactSection, RoleItem } from '@/types';
import i18n from '@/i18n/config';
import type { SortDescriptor } from '@react-types/shared';

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom, useAtomValue } from 'jotai';
import { Button, Chip, Tooltip } from '@heroui/react';
import { RiAddLine, RiEditLine, RiDeleteBinLine, RiTeamLine } from '@remixicon/react';

import { TablePage, TableFilters, ColumnsToggleDropdown, FilterSelect } from '@/components/table';
import { SearchInput } from '@/components/shared/SearchInput';
import { DateTimeDisplay } from '@/components/shared/DateTimeDisplay';
import { PermissionGuard } from '@/components/shared/PermissionGuard';
import { ConfirmDeleteModal } from '@/components/shared/ConfirmDeleteModal';
import { ContactSectionFormModal } from '@/components/contact-sections';
import {
  contactSectionsListParamsAtom,
  contactSectionsQueryAtom,
  rolesHydratedAtom,
  rolesQueryAtom
} from '@/store';
import { useContactSectionsFilters } from '@/hooks/contact-sections';
import { useVisibleColumns } from '@/hooks/table';
import { useContactSectionDelete, useContactSectionForm } from '@/hooks/contact-sections';

export default function ContactSectionsPage() {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useAtomValue(contactSectionsQueryAtom);
  const [params, setParams] = useAtom(contactSectionsListParamsAtom);

  const [, setRolesHydrated] = useAtom(rolesHydratedAtom);
  const { data: rolesResponse } = useAtomValue(rolesQueryAtom);
  const roleOptions: RoleItem[] = rolesResponse?.data ?? [];

  React.useEffect(() => {
    setRolesHydrated(true);
  }, [setRolesHydrated]);

  const {
    searchFields,
    setSearchFields,
    clearFilters,
    handlePageChange,
    hasAnyFilter,
    sortParam,
    setSortParam,
    includeParam,
    setIncludeParam
  } = useContactSectionsFilters();

  React.useEffect(() => {
    if (includeParam !== 'messages') {
      setIncludeParam('messages');
    }
  }, [includeParam, setIncludeParam]);

  const [nameFilter, setNameFilter] = React.useState(searchFields.name || '');
  const [roleFilter, setRoleFilter] = React.useState<string>(searchFields.roles || 'all');

  React.useEffect(() => {
    setNameFilter(searchFields.name || '');
    setRoleFilter(searchFields.roles || 'all');
  }, [searchFields.name, searchFields.roles]);

  const applyFilters = React.useCallback(
    (overrides?: { name?: string; roles?: string }) => {
      const nextName = overrides?.name ?? nameFilter;
      const nextRoles = overrides?.roles ?? roleFilter;
      setSearchFields(() => ({
        name: nextName,
        roles: nextRoles === 'all' ? '' : nextRoles
      }));
    },
    [nameFilter, roleFilter, setSearchFields]
  );

  const handleClearFilters = React.useCallback(() => {
    setNameFilter('');
    setRoleFilter('all');
    clearFilters();
  }, [clearFilters]);

  const [sortDescriptor, setSortDescriptor] = React.useState<SortDescriptor | undefined>(undefined);

  React.useEffect(() => {
    if (!sortParam) {
      setSortDescriptor(undefined);
      return;
    }
    const direction = sortParam.startsWith('-') ? 'descending' : 'ascending';
    const column = sortParam.replace(/^-/, '');
    setSortDescriptor({ column, direction });
  }, [sortParam]);

  const handleSortChange = React.useCallback(
    (descriptor: SortDescriptor) => {
      setSortDescriptor(descriptor);
      const column = descriptor.column?.toString();
      if (!column) {
        setSortParam(null);
        return;
      }
      const prefix = descriptor.direction === 'descending' ? '-' : '';
      setSortParam(`${prefix}${column}`);
    },
    [setSortParam]
  );

  const triggerRefresh = React.useCallback(() => {
    setParams((prev) => ({ ...prev, refresh: Date.now() }));
  }, [setParams]);

  const sections = React.useMemo<ContactSection[]>(() => data?.data ?? [], [data]);

  const {
    isOpen: isSectionFormOpen,
    onOpenChange: onSectionFormOpenChange,
    openCreate,
    openEdit,
    saveSection,
    isSubmitting,
    isEditMode,
    formData,
    setFormData
  } = useContactSectionForm();

  const {
    isOpen: isDeleteOpen,
    onOpenChange: onDeleteOpenChange,
    openDelete,
    confirmDelete,
    isDeleting,
    pendingSection
  } = useContactSectionDelete();

  const handleViewMessages = React.useCallback(
    (sectionId: number) => {
      navigate(`/relationships/contact-messages?filter[section_id]=${sectionId}`);
    },
    [navigate]
  );

  const columns = React.useMemo(
    () => [
      { name: i18n.t('columns.name'), uid: 'name', sortable: true },
      { name: i18n.t('copy.description'), uid: 'description' },
      { name: i18n.t('nav.roles'), uid: 'roles' },
      { name: i18n.t('copy.messages'), uid: 'messages', align: 'center', headerAlign: 'center' },
      { name: i18n.t('copy.created'), uid: 'created_at', sortable: true },
      { name: i18n.t('copy.updated'), uid: 'updated_at', sortable: true },
      { name: i18n.t('common.actions'), uid: 'actions', align: 'end' }
    ],
    []
  );

  const renderCell = React.useCallback(
    (section: ContactSection, columnKey: React.Key) => {
      switch (columnKey) {
        case 'name':
          return (
            <div className="flex flex-col">
              <span className="font-semibold">{section.name}</span>
              <span className="text-default-400 text-xs">#{section.id}</span>
            </div>
          );
        case 'description':
          return section.description ? (
            <p className="text-default-500 line-clamp-2 text-sm">{section.description}</p>
          ) : (
            <span className="text-default-400 text-sm">No description</span>
          );
        case 'roles':
          return section.roles && section.roles.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {section.roles.map((role) => (
                <Chip key={role} size="sm" variant="flat" color="secondary">
                  {role}
                </Chip>
              ))}
            </div>
          ) : (
            <Chip size="sm" variant="flat" color="success">
              All roles
            </Chip>
          );
        case 'messages':
          return (
            <div className="flex justify-center">
              <Button
                size="sm"
                variant="light"
                onPress={() => handleViewMessages(section.id)}
                startContent={<RiTeamLine className="h-4 w-4" />}
              >
                {(section.messages?.length ?? 0).toString()}
              </Button>
            </div>
          );
        case 'created_at':
          return <DateTimeDisplay dateString={section.created_at} />;
        case 'updated_at':
          return <DateTimeDisplay dateString={section.updated_at} />;
        case 'actions':
          return (
            <div className="flex items-center justify-end gap-2">
              <PermissionGuard permission="ContactSection.update">
                <Tooltip content={i18n.t('copy.edit_section')}>
                  <Button isIconOnly size="sm" variant="light" onPress={() => openEdit(section)}>
                    <RiEditLine className="h-4 w-4" />
                  </Button>
                </Tooltip>
              </PermissionGuard>
              <PermissionGuard permission="ContactSection.destroy">
                <Tooltip content={i18n.t('copy.delete_section')}>
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    color="danger"
                    onPress={() => openDelete(section)}
                  >
                    <RiDeleteBinLine className="h-4 w-4" />
                  </Button>
                </Tooltip>
              </PermissionGuard>
            </div>
          );
        default:
          return null;
      }
    },
    [handleViewMessages, openDelete, openEdit]
  );

  const { visibleColumns, setVisibleColumns, headerColumns } = useVisibleColumns(columns);

  return (
    <>
      <TablePage
        columns={columns}
        currentPage={data?.meta?.current_page}
        data={sections}
        emptyMessage={i18n.t('copy.no_contact_sections_found')}
        error={error}
        errorMessage={i18n.t('copy.failed_to_load_contact_sections')}
        title={i18n.t('nav.contactSections')}
        titleActions={
          <PermissionGuard permission="ContactSection.store">
                <Button
                  color="success"
                  size="sm"
                  variant="flat"
                  startContent={<RiAddLine className="h-4 w-4" />}
                  onPress={openCreate}
                >
                  Add Section
            </Button>
          </PermissionGuard>
        }
        filtersSection={
          <TableFilters
            hasActiveFilters={hasAnyFilter}
            isRefreshing={isLoading}
            onClearFilters={handleClearFilters}
            onRefresh={triggerRefresh}
          >
            <SearchInput
              value={nameFilter}
              onValueChange={setNameFilter}
              onSearch={() => applyFilters()}
              onClear={() => {
                setNameFilter('');
                applyFilters({ name: '' });
              }}
            />

            <ColumnsToggleDropdown
              allColumns={columns.map((column) => ({ uid: column.uid, name: column.name }))}
              selected={visibleColumns}
              onChange={setVisibleColumns}
            />

            <FilterSelect
              label={i18n.t('nav.roles')}
              value={roleFilter}
              options={[
                { key: 'all', label: i18n.t('filters.allRoles') },
                ...roleOptions.map((role) => ({ key: role.name, label: role.name }))
              ]}
              onChange={(value) => {
                setRoleFilter(value);
                applyFilters({ roles: value });
              }}
              className="w-48"
            />
          </TableFilters>
        }
        headerColumns={headerColumns}
        isError={isError}
        isLoading={isLoading}
        renderCell={renderCell}
        sortDescriptor={sortDescriptor}
        totalPages={data?.meta?.last_page}
        onPageChange={handlePageChange}
        onRefresh={triggerRefresh}
        onSortChange={handleSortChange}
      />

      <ContactSectionFormModal
        isOpen={isSectionFormOpen}
        onOpenChange={onSectionFormOpenChange}
        formData={formData}
        setFormData={setFormData}
        isSubmitting={isSubmitting}
        onSubmit={saveSection}
        isEditMode={isEditMode}
        roleOptions={roleOptions}
      />

      <ConfirmDeleteModal
        isOpen={isDeleteOpen}
        onOpenChange={onDeleteOpenChange}
        title={i18n.t('copy.delete_contact_section')}
        message={
          pendingSection
            ? `Are you sure you want to delete the "${pendingSection.name}" section?`
            : 'Are you sure you want to delete this section?'
        }
        confirmText={i18n.t('copy.delete_section_1')}
        isLoading={isDeleting}
        onConfirm={confirmDelete}
      />
    </>
  );
}

